import { useEffect, useRef } from 'react';

export interface EditorShortcutHandlers {
  onSave: () => void;
  undo: () => void;
  redo: () => void;
  /** ⌘/Ctrl + J — opens the "Draft with AI" composer. */
  onAi?: () => void;
}

/**
 * Keyboard shortcuts shared by the post and email block editors:
 *
 * - ⌘/Ctrl + S          → save
 * - ⌘/Ctrl + Z          → undo
 * - ⌘/Ctrl + Shift + Z  → redo
 * - ⌘/Ctrl + J          → open the AI composer
 *
 * Handlers are stored in a ref so the window listener is bound exactly once
 * while always invoking the latest callbacks (react-query mutations etc.).
 */
export function useEditorShortcuts(handlers: EditorShortcutHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handlersRef.current.onSave();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handlersRef.current.redo();
        } else {
          handlersRef.current.undo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        handlersRef.current.onAi?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
